import { Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import { site } from '@/mocks/site';
import { testimonials } from '@/mocks/testimonials';

const SITE_URL = 'https://captain2016.cn';

const colorMap: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-800',
  secondary: 'bg-secondary-100 text-secondary-800',
};

export default function About() {
  useSeo({
    title: '关于我们 — 蝌蚪吃瓜时间线团队与内容原则',
    description:
      '了解蝌蚪吃瓜的网站定位、发展理念与内容原则。我们坚持先排时间再下判断，注明来源与分歧，持续更新与修正每一条娱乐事件时间线。',
    keywords: '蝌蚪吃瓜,关于我们,内容原则,时间线,来龙去脉',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于蝌蚪吃瓜',
        url: `${SITE_URL}/about`,
        description: '蝌蚪吃瓜的网站定位、发展理念与内容原则。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: site.name,
        url: SITE_URL,
        email: site.email,
        slogan: site.slogan,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_URL}/about` },
        ],
      },
    ],
  });

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="mx-auto w-full px-4 md:px-6">
          <Breadcrumbs items={[{ label: '关于我们' }]} />
          <div className="mt-5 max-w-3xl">
            <SectionHeading
              kicker="关于我们"
              title="把一个瓜，讲成一条能看懂的线"
              description={site.intro}
            />
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-2 lg:items-center">
          <div className="h-64 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20workspace%20with%20open%20notebooks%20and%20a%20timeline%20sketch%20on%20paper%2C%20amber%20desk%20lamp%20glow%2C%20brick%20red%20accents%2C%20cozy%20writing%20studio%2C%20no%20text%20no%20people&width=1200&height=800&seq=chigua-about-01&orientation=landscape"
              alt="蝌蚪吃瓜 编辑整理事件时间线的工作场景"
              title="蝌蚪吃瓜 - 时间线整理"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">品牌介绍</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              蝌蚪吃瓜是一个专注娱乐事件复盘的内容站点。我们不做「一句话结论」，而是把每件事拆成节点，按发生时间排好，再讨论节点之间的因果。读者不需要在十几条热搜之间来回跳，只需要顺着一条线读下去。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              站内内容由编辑团队整理，结构统一为「时间线 + 前因 + 经过 + 当后果」。事件仍在发展时，时间线会持续追加新节点，并在文首标注最后更新时间。
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-4">
              {site.stats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <dt className="font-label text-xs text-foreground-500">{stat.label}</dt>
                  <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">{stat.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6">
          <SectionHeading
            kicker="网站定位"
            title="我们只做三件事"
            description="围绕时间线，把「记录」「核对」「更新」做到位，其余热闹的部分交给你自己判断。"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              {
                icon: 'ri-list-ordered',
                title: '记录节点',
                desc: '把散落在各处的公开信息重新排成一条时间线，标出起点、发酵、回应与结局。',
                tone: 'primary',
              },
              {
                icon: 'ri-shield-check-line',
                title: '核对来源',
                desc: '每条节点标注出现时间与出处，存在争议的说法单独列出，不与已确认内容混排。',
                tone: 'secondary',
              },
              {
                icon: 'ri-loop-right-line',
                title: '持续更新',
                desc: '事件没结束就继续追加节点，发现错误就修订并保留说明，让时间线始终跟得上。',
                tone: 'accent',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span
                  className={`flex h-11 w-11 items-center justify-center rounded-md ${
                    item.tone === 'primary'
                      ? 'bg-primary-100 text-primary-600'
                      : item.tone === 'secondary'
                        ? 'bg-secondary-100 text-secondary-700'
                        : 'bg-accent-100 text-accent-800'
                  }`}
                >
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6">
          <SectionHeading
            kicker="内容原则"
            title="发展理念"
            description="这三条原则决定了哪些内容会出现在时间线上，也决定了我们如何面对信息的不确定性。"
          />
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {site.principles.map((item, index) => (
              <article key={item.title} className="relative rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="font-heading text-4xl font-bold text-background-300">0{index + 1}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6">
          <SectionHeading kicker="用户口碑" title="读者怎么说" align="center" />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-accent-400" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.content}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-full font-heading text-base font-bold ${
                      colorMap[item.color] ?? colorMap.primary
                    }`}
                  >
                    {item.name.slice(0, 1)}
                  </span>
                  <span className="flex flex-col">
                    <span className="font-label text-sm text-foreground-950">{item.name}</span>
                    <span className="font-label text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto flex w-full flex-col items-center gap-6 rounded-lg border border-primary-200 bg-primary-50 px-6 py-12 text-center md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            想知道某件事到底怎么发生的？
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
            可以把线索、疑问或者更正直接发给我们，编辑会在核对后决定是否整理成新的时间线。投稿时记得附上信息出现的时间与来源。
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-mail-send-line" />
              联系我们
            </Link>
            <Link
              to="/timeline"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-3 font-label text-sm text-primary-700 transition-colors hover:bg-background-100"
            >
              浏览事件合集
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <p className="font-label text-xs text-foreground-500">
            联系邮箱：{site.email} · 内容最后更新：{site.updateTime}
          </p>
        </div>
      </section>
    </div>
  );
}